import useInfiniteScrollTable from 'common/components/BasicTable/useInfiniteScrollTable'
import PageWrapper from 'common/components/PageWrapper'
import { CRUDAction, FeatureKey } from 'common/constants/permission'
import { useMutateEditStatusWarehouse } from 'modules/inventory/api/mutation'
import { INITIAL_PAGE_SIZE, useWarehouse } from 'modules/inventory/api/queries'
import type { Warehouse } from 'modules/inventory/api/type'
import { GreenSwitch } from 'modules/inventory/components/IsActiveSwitch'
import WarehouseTools from 'modules/inventory/components/WarehouseTools'
import { formatThaiShortBE } from 'modules/inventory/helper/format_date'
import CreateWarehouseDialog from 'modules/inventory/pages/warehouse/CreateWarehouse'
import { usePermitSomeActionToFeature, usePermitToActionFeature } from 'modules/user/api/selector'
import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { PATH_DASHBOARD } from 'routes/paths'

import AddIcon from '@mui/icons-material/Add'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import LockIcon from '@mui/icons-material/Lock'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import {
  Avatar,
  Box,
  Button,
  Card,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'

import NoDataNoBorder from 'components/NoDataNoBorder'

const WarehousePage = () => {
  const { t } = useTranslation('warehouse')
  const theme = useTheme()
  const navigate = useNavigate()
  const showAddWarehouse = usePermitToActionFeature([CRUDAction.Create], FeatureKey.Inventory)
  const canManageWarehouse = usePermitSomeActionToFeature(
    [CRUDAction.Update, CRUDAction.Delete],
    FeatureKey.Inventory
  )
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [selectedRow, setSelectedRow] = useState<Warehouse | null>(null)
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set())
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  const containerRef = useRef<HTMLDivElement | null>(null)
  const updateStatusMutation = useMutateEditStatusWarehouse()
  const { data, isLoading, hasNextPage, fetchNextPage } = useWarehouse()

  const warehouses = data?.pages?.flatMap((page) => page.warehouses || []) || []

  const handleScroll = useInfiniteScrollTable({
    isLoading,
    onLoading: () => { if (hasNextPage) fetchNextPage() },
  })

  const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement>, row: Warehouse) => {
    setAnchorEl(e.currentTarget)
    setSelectedRow(row)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
    setSelectedRow(null)
  }

  const handleToggleActive = (warehouseId: string, currentIsActive: boolean) => {
    setTogglingIds((prev) => new Set(prev).add(warehouseId))
    updateStatusMutation.mutate(
      { body: { is_active: !currentIsActive }, warehouseId },
      {
        onSettled: () =>
          setTogglingIds((prev) => {
            const next = new Set(prev)
            next.delete(warehouseId)
            return next
          }),
      }
    )
  }

  const LINKS = [
    { name: t('inventory.title', { defaultValue: 'คลัง' }), href: '/dashboard/product' },
    { name: t('inventory.settings', { defaultValue: 'ตั้งค่าคลัง' }), href: '/dashboard/inventory/settings' },
    { name: t('warehouse.title') },
  ]

  React.useEffect(() => {
    containerRef.current?.scrollTo({ top: 0 })
  }, [])

  return (
    <PageWrapper
      title={
        <Stack direction="row" spacing={0.5} alignItems="center">
          <IconButton size="small" onClick={() => navigate(PATH_DASHBOARD.inventory.settings)}>
            <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography variant="h4">{t('warehouse.title')}</Typography>
        </Stack>
      }
      titleHead={t('warehouse.title')}
      links={LINKS}
      action={
        showAddWarehouse && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            {t('warehouse.create.new', { defaultValue: 'สร้างโกดัง' })}
          </Button>
        )
      }
    >
      <Card
        sx={{
          width: '100%',
          bgcolor: 'common.white',
          borderRadius: '24px',
          p: 3,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          {t('warehouse.detail', { defaultValue: 'รายละเอียดโกดัง' })}
        </Typography>

        {isLoading ? (
          <NoDataNoBorder />
        ) : warehouses.length > 0 ? (
          <TableContainer
            ref={containerRef}
            component={Paper}
            onScroll={handleScroll}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            <Table
              stickyHeader
              sx={{
                '& thead th': {
                  fontSize: '14px',
                  color: theme.palette.common.black,
                  backgroundColor: '#F0F0F0',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                },
                '& tbody td': { borderBottom: '1px solid', borderColor: 'divider' },
                '& thead th:first-of-type': { borderTopLeftRadius: 12 },
                '& thead th:last-of-type': { borderTopRightRadius: 12 },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('warehouse.createdAt')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('warehouse.name')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('warehouse.type', { defaultValue: 'ประเภทโกดัง' })}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('warehouse.farm', { defaultValue: 'สถานที่' })}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {warehouses.map((row, idx) => (
                  <TableRow key={row.id || idx} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatThaiShortBE(row.created_at)}
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          variant="rounded"
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 1,
                            bgcolor: 'ButtonHighlight',
                            color: 'success.dark',
                          }}
                        >
                          <Box
                          component="img"
                          src="/static/components/warehouse.png"
                          alt="Farm login"
                          sx={{
                            objectFit: 'cover',
                            objectPosition: 'center',
                          }}
                        />
                        </Avatar>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {row.is_default && (
                            <LockIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          )}
                          <Typography variant="body2">{row.name || '-'}</Typography>
                        </Stack>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {row.is_default
                          ? t('warehouse.type.default', { defaultValue: 'โกดังเริ่มต้น' })
                          : t('warehouse.type.custom', { defaultValue: 'โกดังกำหนดเอง' })}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {row.farm_name || '-'}
                      </Typography>
                    </TableCell>


                    <TableCell align="right">
                      {!row.is_default && canManageWarehouse && (
                        <>
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenMenu(e, row)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                          <WarehouseTools
                            onClose={handleCloseMenu}
                            open={Boolean(anchorEl) && selectedRow?.id === row.id}
                            anchorEl={anchorEl}
                            warehouseId={selectedRow?.id as string}
                            warehouseIdItem={selectedRow as Warehouse}
                          />
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <NoDataNoBorder />
        )}
      </Card>
      <CreateWarehouseDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
    </PageWrapper>
  )
}

export default WarehousePage
